export type OverrideMissionWarningOptions = {
  isShown: boolean;
  missionId: string | null;
  vehicleId: string | null;
  vehicleName: string | null;
  vehicleTypeId: string | null;
};

/**
 * Filters missions data based on search value
 * @param missions - Array of missions to filter
 * @param searchValue - Search term to filter by
 * @returns Filtered array of missions
 */
export const filterMissionsDataBySearch = (
  missions: Mission[],
  searchValue: string
): Mission[] => {
  if (!searchValue) return missions;

  const lowerSearch = searchValue.toLowerCase();
  return missions.filter((mission) =>
    mission.name?.toLowerCase().includes(lowerSearch)
  );
};

/**
 * Finds a vehicle by ID
 * @param vehicles - Array of vehicles
 * @param vehicleId - ID of the vehicle to find
 * @returns Vehicle object or undefined
 */
export const findVehicleById = (
  vehicles: Vehicle[],
  vehicleId: string
): Vehicle | undefined => {
  return vehicles.find((vehicle) => vehicle?.id === vehicleId);
};

/**
 * Checks if a vehicle is in sync state
 * @param vehiclesSyncStateById - Record of vehicle sync states
 * @param vehicleId - ID of the vehicle to check
 * @returns Boolean indicating if vehicle is syncing
 */
export const isVehicleSyncing = (
  vehiclesSyncStateById: Record<string, boolean>,
  vehicleId: string
): boolean => {
  return Boolean(vehiclesSyncStateById[vehicleId]);
};

/**
 * Creates override mission warning options
 * @param missionId - ID of the mission
 * @param vehicle - Vehicle object
 * @returns Override mission warning options
 */
export const createOverrideMissionWarningOptions = (
  missionId: string,
  vehicle: Vehicle | undefined
): OverrideMissionWarningOptions => {
  return {
    isShown: true,
    missionId: missionId || null,
    vehicleId: vehicle?.id || null,
    vehicleName: vehicle?.name || null,
    vehicleTypeId: vehicle?.vehicleTypeId || null,
  };
};

/**
 * Resets override mission warning options
 * @returns Reset override mission warning options
 */
export const resetOverrideMissionWarningOptions =
  (): OverrideMissionWarningOptions => {
    return {
      isShown: false,
      missionId: null,
      vehicleId: null,
      vehicleName: null,
      vehicleTypeId: null,
    };
  };

/**
 * Validates if mission data is complete (for Mission type)
 * @param mission - Mission object to validate
 * @returns Boolean indicating if mission is valid
 */
export const isValidMissionData = (mission: Mission): boolean => {
  return Boolean(mission.id && mission.name && (mission as any).status);
};

/**
 * Validates if override mission warning options are complete
 * @param options - Override mission warning options
 * @returns Boolean indicating if options are valid for override
 */
export const canOverrideMission = (
  options: OverrideMissionWarningOptions
): boolean => {
  return Boolean(
    options.vehicleId && options.vehicleName && options.vehicleTypeId
  );
};

/**
 * Creates mission cancellation data
 * @param missionId - ID of the mission to cancel
 * @param vehicleId - ID of the vehicle
 * @returns Mission cancellation data
 */
export const createMissionCancellationData = (
  missionId: string,
  vehicleId: string
): { missionId: string; vehicleId: string } => {
  return {
    missionId,
    vehicleId,
  };
};

/**
 * Gets the display-order key for a mission, preferring the server-computed
 * displayAt (which accounts for OVM positioning) over deployedAt/timeStamp.
 */
export const getMissionQueueSortKey = (mission: Mission): string | null => {
  return (
    mission?.displayAt || mission?.deployedAt || mission?.timeStamp || null
  );
};

/**
 * Gets the deployment date for a mission with fallback to timeStamp
 * @param mission - Mission object
 * @returns Deployment date string or null
 */
export const getMissionDeploymentDate = (mission: Mission): string | null => {
  return mission?.deployedAt || mission?.timeStamp || null;
};

/**
 * Sorts missions by activity date in descending order (most recently active
 * first). Uses getMissionActivityDate (startedExecutionAt, falling back to
 * deployment/creation date) — the same key as dedupeMissionsByVehicle — so the
 * mission kept for a vehicle and its position in the list are decided
 * consistently.
 * @param missions - Array of missions to sort
 * @returns Sorted array of missions
 */
export const sortMissionsByActivityDate = (missions: Mission[]): Mission[] => {
  return missions.sort((a, b) => {
    const aActivityDate = getMissionActivityDate(a);
    const bActivityDate = getMissionActivityDate(b);

    // Handle cases where dates might be null/undefined
    if (!aActivityDate && !bActivityDate) return 0;
    if (!aActivityDate) return 1; // Put missions without a date at the end
    if (!bActivityDate) return -1;

    // Convert to Date objects and compare
    const dateA = new Date(aActivityDate);
    const dateB = new Date(bActivityDate);

    // Descending order (most recent first)
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * Sorts missions by deployment/display date in descending order (most recent
 * first). Uses getMissionQueueSortKey (displayAt-aware, DEV-1629) so the OVM
 * queue display ordering is preserved. Used by the missionsPage / missionPlanner
 * WS insert reducers.
 * @param missions - Array of missions to sort
 * @returns Sorted array of missions
 */
export const sortMissionsByDeploymentDate = (
  missions: Mission[]
): Mission[] => {
  return missions.sort((a, b) => {
    const aDeployedAt = getMissionQueueSortKey(a);
    const bDeployedAt = getMissionQueueSortKey(b);

    // Handle cases where dates might be null/undefined
    if (!aDeployedAt && !bDeployedAt) return 0;
    if (!aDeployedAt) return 1; // Put missions without deployedAt at the end
    if (!bDeployedAt) return -1;

    // Convert to Date objects and compare
    const dateA = new Date(aDeployedAt);
    const dateB = new Date(bDeployedAt);

    // Descending order (most recent first)
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * Recency signal for "which live mission is the vehicle currently on".
 * Prefers startedExecutionAt — the moment the vehicle actually began running
 * the mission — because for two simultaneously-live missions the one it started
 * most recently is the real current job. Falls back to the deployment/creation
 * date (deployedAt === timeStamp on the backend) only when startedExecutionAt
 * is absent, so the comparison is always defined.
 * @param mission - Mission object
 * @returns Date string or null
 */
export const getMissionActivityDate = (mission: Mission): string | null => {
  return mission?.startedExecutionAt || getMissionDeploymentDate(mission);
};

/**
 * De-duplicates missions so each vehicle appears at most once, keeping the
 * mission the vehicle most recently started executing (see
 * getMissionActivityDate). A vehicle should only ever have one live mission;
 * this guards the page against transient backend states where two are reported
 * simultaneously. Missions without a vehicle are left untouched (nothing to
 * dedupe against).
 * @param missions - Array of missions to de-duplicate
 * @returns De-duplicated array of missions
 */
export const dedupeMissionsByVehicle = (missions: Mission[]): Mission[] => {
  const newestByVehicle = new Map<string, Mission>();
  const unassigned: Mission[] = [];

  missions.forEach((mission) => {
    const vehicleId = (mission as any).vehicle as string | null | undefined;

    if (!vehicleId) {
      unassigned.push(mission);
      return;
    }

    const existing = newestByVehicle.get(vehicleId);
    if (!existing) {
      newestByVehicle.set(vehicleId, mission);
      return;
    }

    // More recently active mission wins; a missing date is treated as oldest.
    const existingDate = getMissionActivityDate(existing);
    const candidateDate = getMissionActivityDate(mission);
    const existingTime = existingDate
      ? new Date(existingDate).getTime()
      : -Infinity;
    const candidateTime = candidateDate
      ? new Date(candidateDate).getTime()
      : -Infinity;

    if (candidateTime >= existingTime) {
      newestByVehicle.set(vehicleId, mission);
    }
  });

  return [...unassigned, ...newestByVehicle.values()];
};

/**
 * Counts total missions by status (for Mission type)
 * @param missions - Array of missions
 * @returns Record with status counts
 */
export const countMissionsByStatusData = (
  missions: Mission[]
): Record<string, number> => {
  return missions.reduce((acc, mission) => {
    const status = mission.status;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
};
